import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { DailySchedule, UserRole } from '../types/schedule';
import { AcademyId } from '../types/academy';
import type { AccessLevel } from '../types/academy';
import { isAdPrivacyOptionsRequired, showAdPrivacyOptions } from './AdMobBanner';
import NotificationPrompt from './NotificationPrompt';
import { UpdateSettingsCard } from '../features/app-update/AppUpdateProvider';

interface Props {
  role: UserRole;
  academy: AcademyId;
  allAcademies?: boolean;
  accessLevel?: AccessLevel;
  academyControl?: ReactNode;
  cycleName?: string | null;
  schedules: DailySchedule[];
  darkMode: boolean;
  onDarkModeChange: (enabled: boolean) => void;
  onLogout: () => void;
  onOpenAppGuide?: () => void;
  onDeleteCycle: (cycleName: string) => void;
  onResetSchedules: () => void;
  testMode?: boolean;
  tvDisplay?: boolean;
  notificationsDisabled?: boolean;
  testSessionControl?: ReactNode;
}

const Toggle = ({ enabled, onChange, label }: { enabled: boolean; onChange: () => void; label: string }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    aria-label={label}
    onClick={onChange}
    className={`relative h-7 w-12 rounded-full transition-colors ${enabled ? 'bg-green-600' : 'bg-gray-300'}`}
  >
    <span className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

export default function GeneralSettings({
  role,
  academy,
  allAcademies = false,
  accessLevel,
  academyControl,
  cycleName,
  schedules,
  darkMode,
  onDarkModeChange,
  onLogout,
  onOpenAppGuide,
  onDeleteCycle,
  onResetSchedules,
  testMode = false,
  tvDisplay = false,
  notificationsDisabled = false,
  testSessionControl
}: Props) {
  const [open, setOpen] = useState(false);
  const [showDatabase, setShowDatabase] = useState(false);
  const [showAdPrivacy, setShowAdPrivacy] = useState(false);
  const [adPrivacyBusy, setAdPrivacyBusy] = useState(false);
  const [adPrivacyError, setAdPrivacyError] = useState(false);
  const settingsButtonBottom = Capacitor.isNativePlatform()
    ? Capacitor.getPlatform() === 'android'
      ? 'calc(var(--native-banner-height, 50px) + env(safe-area-inset-bottom, 0px) + 8px)'
      : 'calc(var(--native-banner-height, 50px) + 8px)'
    : 'calc(1rem + env(safe-area-inset-bottom))';
  const uniqueCycles = useMemo(() => Array.from(new Set(
    schedules.map(schedule => schedule.cycleName?.trim()).filter((value): value is string => Boolean(value))
  )).sort(), [schedules]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    isAdPrivacyOptionsRequired()
      .then(required => {
        if (!cancelled) setShowAdPrivacy(required);
      })
      .catch(error => console.error('Failed to check AdMob privacy options:', error));

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleAdPrivacyChoices = async () => {
    if (adPrivacyBusy) return;

    setAdPrivacyBusy(true);
    setAdPrivacyError(false);
    try {
      await showAdPrivacyOptions();
      setShowAdPrivacy(await isAdPrivacyOptionsRequired());
    } catch (error) {
      console.error('Failed to show AdMob privacy options:', error);
      setAdPrivacyError(true);
    } finally {
      setAdPrivacyBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="settings-launcher fixed left-3 z-[70] flex h-12 w-12 items-center justify-center rounded-full bg-gray-700 text-white shadow-xl ring-2 ring-white/70 active:bg-gray-600 sm:left-5"
        style={{ bottom: settingsButtonBottom }}
        aria-label="Open settings"
        aria-haspopup="dialog"
      >
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>

      <div
        className={`settings-overlay fixed inset-0 z-[80] items-end justify-center overflow-y-auto bg-black/55 px-0 pt-4 backdrop-blur-sm sm:items-center sm:p-4 ${open ? 'flex' : 'hidden'}`}
        aria-hidden={!open}
      >
          <div className="settings-panel w-full overflow-y-auto overscroll-contain rounded-t-3xl bg-white p-5 pb-8 shadow-2xl sm:max-w-md sm:rounded-3xl">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-gray-900">Settings</h2>
                <p className="text-xs font-bold text-gray-500">{tvDisplay ? 'TV Display' : accessLevel === 'NCOA_MANAGER' ? 'NCOA Commander' : role === 'ADMIN' ? 'Chief' : role === 'VIEWER' ? 'SGL' : 'Student'}</p>
              </div>
              <button onClick={() => setOpen(false)} className="rounded-full bg-gray-100 p-2 text-gray-600" aria-label="Close settings">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="space-y-3">
              <UpdateSettingsCard />
              {onOpenAppGuide && (
                <button type="button" className="settings-card flex w-full items-center justify-between gap-4 rounded-2xl border border-blue-200 p-4 text-left"
                  onClick={() => { setOpen(false); onOpenAppGuide(); }}>
                  <span><span className="block text-sm font-black text-gray-900">App guide</span>
                    <span className="block text-[11px] font-semibold text-gray-500">Explore the features available to you</span></span>
                  <span className="text-xs font-black text-blue-800">VIEW</span>
                </button>
              )}
              {testMode && testSessionControl}

              {academyControl && allAcademies && (
                <section className="settings-card flex items-center justify-between gap-4 rounded-2xl border border-gray-200 p-4">
                  <div>
                    <div className="text-sm font-black text-gray-900">Academy</div>
                    <div className="text-[11px] font-semibold text-gray-500">Switch between BLC and KTA schedules</div>
                  </div>
                  {academyControl}
                </section>
              )}

              <section className="settings-card flex items-center justify-between gap-3 rounded-2xl border border-gray-200 p-4">
                <div className="shrink-0">
                  <div className="text-sm font-black text-gray-900">Notifications</div>
                  <div className="text-[11px] font-semibold text-gray-500">Schedule update alerts</div>
                </div>
                <div className="min-w-0 flex-1 text-right">
                  {notificationsDisabled ? <span className="text-xs font-bold text-gray-500">Disabled in local emulator</span> : <NotificationPrompt role={role} academy={academy} allAcademies={allAcademies} cycleName={cycleName} variant="toggle" autoPrompt={false} testMode={testMode} />}
                </div>
              </section>

              <section className="settings-card flex items-center justify-between rounded-2xl border border-gray-200 p-4">
                <div>
                  <div className="text-sm font-black text-gray-900">Dark Mode</div>
                  <div className="text-[11px] font-semibold text-gray-500">Use a darker app theme</div>
                </div>
                <Toggle enabled={darkMode} onChange={() => onDarkModeChange(!darkMode)} label="Dark mode" />
              </section>

              {showAdPrivacy && (
                <section className="settings-card rounded-2xl border border-gray-200 p-4">
                  <button
                    type="button"
                    onClick={handleAdPrivacyChoices}
                    disabled={adPrivacyBusy}
                    className="flex w-full items-center justify-between gap-4 text-left disabled:opacity-60"
                  >
                    <div>
                      <div className="text-sm font-black text-gray-900">Ad Privacy Choices</div>
                      <div className="text-[11px] font-semibold text-gray-500">Review or change your advertising consent</div>
                    </div>
                    <span className="shrink-0 text-xs font-black text-blue-800">{adPrivacyBusy ? 'OPENING...' : 'OPEN'}</span>
                  </button>
                  {adPrivacyError && (
                    <p className="mt-2 text-[11px] font-semibold text-red-600">Privacy choices could not be opened. Please try again.</p>
                  )}
                </section>
              )}

              {role === 'ADMIN' && (
                <section className="settings-card rounded-2xl border border-red-200 p-4">
                  <button onClick={() => setShowDatabase(value => !value)} className="flex w-full items-center justify-between text-left">
                    <div>
                      <div className="text-sm font-black text-red-800">Cycle Database Management</div>
                      <div className="text-[11px] font-semibold text-gray-500">Import or remove schedule data</div>
                    </div>
                    <span className="text-xs font-black text-red-700">{showDatabase ? 'CLOSE' : 'OPEN'}</span>
                  </button>
                  {showDatabase && (
                    <div className="mt-4 space-y-3 border-t border-red-100 pt-4">
                      <div className="max-h-40 space-y-1 overflow-y-auto">
                        {uniqueCycles.map(cycle => (
                          <div key={cycle} className="flex items-center justify-between rounded-lg bg-gray-100 px-3 py-2">
                            <span className="text-xs font-bold text-gray-700">Cycle {cycle}</span>
                            <button onClick={() => onDeleteCycle(cycle)} className="rounded-lg px-2 py-1 text-xs font-black text-red-700">DELETE</button>
                          </div>
                        ))}
                        {uniqueCycles.length === 0 && <p className="text-xs font-semibold text-gray-400">No cycle data found.</p>}
                      </div>
                      <button onClick={onResetSchedules} className="w-full rounded-xl bg-red-700 py-3 text-xs font-black text-white">
                        CLEAR ALL DATABASE
                      </button>
                    </div>
                  )}
                </section>
              )}

              <section className="settings-card rounded-2xl border border-gray-200 p-4">
                <div className="mb-3">
                  <div className="text-sm font-black text-gray-900">Help &amp; Legal</div>
                  <div className="text-[11px] font-semibold text-gray-500">Get help or review our privacy policy</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href="/support.html"
                    className="help-support-button rounded-xl bg-blue-50 px-3 py-2.5 text-center text-xs font-black text-blue-900"
                  >
                    SUPPORT
                  </a>
                  <a
                    href="/privacy.html"
                    className="help-privacy-button rounded-xl bg-gray-100 px-3 py-2.5 text-center text-xs font-black text-gray-700"
                  >
                    PRIVACY POLICY
                  </a>
                </div>
              </section>

              <button onClick={onLogout} className="w-full rounded-2xl bg-gray-800 py-3 text-sm font-black text-white">
                LOGOUT
              </button>
            </div>
          </div>
      </div>
    </>
  );
}
